import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Shield, ChevronLeft, ChevronRight, Sparkles, Lock,
  Bookmark, BookmarkX, Loader2, NotebookPen, Check,
} from 'lucide-react';
import { dailyFocusBlocksApi, calendarApi } from '../api/services';
import type { DailyFocusBlock, CalendarEvent } from '../types/models';
import { focusTimeCopy } from '../copy/focusTime';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useToast } from '@/components/ui/toast';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7);
const ROW_HEIGHT = 36;
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 20;
const TOTAL_MINUTES = (DAY_END_HOUR - DAY_START_HOUR) * 60;
const GRID_HEIGHT = (DAY_END_HOUR - DAY_START_HOUR) * ROW_HEIGHT;

// Redesign rule: focus = blue (all sources read as protected),
// meeting = neutral grey, conflict = red ONLY. Previously meetings
// rendered in red tones which made the grid read as a sea of warnings
// before any actual conflict existed. Source variants stay within the
// blue family so the eye still reads them as one thing — protected
// time — while a subtle hue difference distinguishes them up close.
const SOURCE_COLOURS: Record<string, string> = {
  AutoAllocated: 'rgba(108, 155, 255, 0.35)',
  Manual: 'rgba(125, 177, 255, 0.42)',
  DetectedFromCalendar: 'rgba(169, 203, 255, 0.34)',
};
const MEETING_COLOUR = 'rgba(140, 152, 170, 0.22)';
const CONFLICT_COLOUR = 'rgba(255, 82, 82, 0.5)';

function timeStrToHour(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
}

function formatHour(hour: number): string {
  const h = Math.floor(hour);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const display = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${display} ${ampm}`;
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDateOnly(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatShortDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function addDays(d: Date, n: number): Date {
  const result = new Date(d);
  result.setDate(result.getDate() + n);
  return result;
}

function sourceLabel(source: string): string {
  switch (source) {
    case 'AutoAllocated': return 'Auto';
    case 'Manual': return 'Manual';
    case 'DetectedFromCalendar': return 'Detected';
    default: return source;
  }
}

export default function FocusTimePage() {
  const confirm = useConfirm();
  const { toast } = useToast();
  const [weekStart, setWeekStart] = useState<Date>(() => getMonday(new Date()));
  const [focusBlocks, setFocusBlocks] = useState<DailyFocusBlock[]>([]);
  const [gridEvents, setGridEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDate, setAddDate] = useState('');
  const [addStart, setAddStart] = useState('09:00');
  const [addEnd, setAddEnd] = useState('11:00');
  const [addLabel, setAddLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Conflict + meeting popovers: the controlled-open block id keeps each
  // popover anchored to the specific cell the user clicked, so multiple
  // events on the same day don't fight over one shared Popover instance.
  const [conflictOpenId, setConflictOpenId] = useState<string | null>(null);
  const [meetingOpenId, setMeetingOpenId] = useState<string | null>(null);
  // Per-block details popover anchored to the clicked focus block. Shows
  // the work items SchedulingEngine packed inside plus an editable Notes
  // field. Only one open at a time; tracked by id like the other two.
  const [noteOpenId, setNoteOpenId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState<string>('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [acknowledgedConflicts, setAcknowledgedConflicts] = useState<Set<string>>(() => new Set());
  const [snackbar, setSnackbar] = useState<string | null>(null);

  useEffect(() => {
    if (!snackbar) return;
    const t = setTimeout(() => setSnackbar(null), 4000);
    return () => clearTimeout(t);
  }, [snackbar]);

  const weekDates = DAYS.map((_, i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 6);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [blocks, events] = await Promise.all([
        dailyFocusBlocksApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
        calendarApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
      ]);
      setFocusBlocks(blocks);
      setGridEvents(events.filter(e => !e.isAllDay));
    } catch (err) {
      // silent-by-design: reloads on every week navigation; toast spam.
      console.error('Failed to load focus data', err);
    } finally {
      setLoading(false);
    }
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Delete focus block?',
      body: 'The block and its matching calendar event will be removed. Any work items scheduled into it will return to the unscheduled list.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await dailyFocusBlocksApi.delete(id);
      setFocusBlocks(prev => prev.filter(b => b.id !== id));
    } catch (err: any) {
      console.error('Failed to delete focus block', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to delete focus block' });
    }
  };

  const handleKeepFocus = (blockId: string) => {
    setAcknowledgedConflicts((prev) => {
      const next = new Set(prev);
      next.add(blockId);
      return next;
    });
    setConflictOpenId(null);
  };

  const handleRemoveFocus = async (blockId: string) => {
    setConflictOpenId(null);
    try {
      await dailyFocusBlocksApi.delete(blockId);
      setFocusBlocks((prev) => prev.filter((b) => b.id !== blockId));
    } catch (err: any) {
      console.error('Failed to remove focus block', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to remove focus block' });
    }
  };

  // Open the per-block details popover and seed the notes draft from the
  // block's current notes. Caller is the cell's PopoverTrigger onClick.
  const openBlockDetails = (block: DailyFocusBlock) => {
    setNoteOpenId(block.id);
    setNoteDraft(block.notes ?? '');
  };

  const saveBlockNotes = async (block: DailyFocusBlock) => {
    setNoteSaving(true);
    try {
      const trimmed = noteDraft.trim();
      const updated = await dailyFocusBlocksApi.update(block.id, {
        date: block.date,
        startTime: block.startTime,
        endTime: block.endTime,
        label: block.label,
        // Empty string clears the field on the backend; non-empty saves
        // the trimmed text. `null` would skip the update — that's the
        // "leave alone" path the update endpoint uses, not what we want
        // when the user has explicitly opened the editor.
        notes: trimmed,
      });
      setFocusBlocks(prev => prev.map(b => b.id === block.id ? { ...b, notes: updated.notes ?? null } : b));
      setNoteOpenId(null);
      toast({ variant: 'success', message: trimmed ? 'Notes saved' : 'Notes cleared' });
    } catch (err: any) {
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to save notes' });
    } finally {
      setNoteSaving(false);
    }
  };

  const handleMarkMeetingAsFocus = async (evt: CalendarEvent) => {
    setMeetingOpenId(null);
    try {
      const block = await dailyFocusBlocksApi.markMeetingAsFocus(evt.id);
      setFocusBlocks((prev) =>
        prev.some((b) => b.id === block.id) ? prev : [...prev, block]
      );
      setSnackbar(`Marked "${evt.title}" as focus time.`);
    } catch (err) {
      // Surfaced via the existing snackbar — no toast duplication.
      console.error('Failed to mark meeting as focus', err);
      setSnackbar('Could not mark meeting as focus. Please try again.');
    }
  };

  const handleUnmarkMeetingAsFocus = async (evt: CalendarEvent) => {
    setMeetingOpenId(null);
    try {
      await dailyFocusBlocksApi.unmarkMeetingAsFocus(evt.id);
      setFocusBlocks((prev) => prev.filter((b) => b.linkedCalendarEventId !== evt.id));
      setSnackbar(`Removed focus mark from "${evt.title}".`);
    } catch (err) {
      // Surfaced via the existing snackbar — no toast duplication.
      console.error('Failed to unmark meeting as focus', err);
      setSnackbar('Could not remove focus mark. Please try again.');
    }
  };

  const openAddDialog = (date?: string) => {
    setAddDate(date ?? formatDateOnly(weekDates[0]));
    setAddStart('09:00');
    setAddEnd('11:00');
    setAddLabel('');
    setAddError(null);
    setAddDialogOpen(true);
  };

  const handleSaveBlock = async () => {
    if (!addDate || !addStart || !addEnd) return;
    if (addEnd <= addStart) { setAddError('End time must be after start time.'); return; }
    setSaving(true);
    setAddError(null);
    try {
      const block = await dailyFocusBlocksApi.create({
        date: addDate,
        startTime: addStart,
        endTime: addEnd,
        label: addLabel || 'Focus Time',
      });
      setFocusBlocks(prev => [...prev, block]);
      setAddDialogOpen(false);
    } catch {
      setAddError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Build lookups for the grid
  const blocksByDateStr: Record<string, DailyFocusBlock[]> = {};
  weekDates.forEach(d => { blocksByDateStr[formatDateOnly(d)] = []; });
  focusBlocks.forEach(b => {
    if (blocksByDateStr[b.date]) blocksByDateStr[b.date].push(b);
  });

  type GridMeeting = {
    id: string;
    startMinute: number;
    endMinute: number;
    title: string;
    isMarkedAsFocus: boolean;
  };
  const meetingsByDateStr: Record<string, GridMeeting[]> = {};
  weekDates.forEach(d => { meetingsByDateStr[formatDateOnly(d)] = []; });

  // Set of calendar event ids whose meeting representation should be hidden
  // because a focus block already represents that slot. Otherwise the grid
  // double-renders the same event (focus block + meeting on top) producing
  // stacked labels.
  const linkedEventIds = (() => {
    const set = new Set<string>();
    focusBlocks.forEach(b => {
      if (b.linkedCalendarEventId) set.add(b.linkedCalendarEventId);
    });
    if (focusBlocks.some(b => b.source === 'DetectedFromCalendar')) {
      gridEvents.forEach(evt => {
        const start = new Date(evt.startTime);
        const end = new Date(evt.endTime);
        const evDate = formatDateOnly(start);
        const evStart = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
        const evEnd = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
        const matchesDetected = focusBlocks.some(b =>
          b.source === 'DetectedFromCalendar'
          && b.date === evDate
          && b.startTime.startsWith(evStart)
          && b.endTime.startsWith(evEnd),
        );
        if (matchesDetected) set.add(evt.id);
      });
    }
    return set;
  })();

  // Mirrors CalendarFocusDetection's title patterns on the server — client-side
  // detection lets the grid suppress duplicate renders for focus-shaped
  // meetings without round-tripping through the server. "Personal", "Blocked"
  // and "Hold" aren't focus words: those events are busy unless marked as focus.
  const FOCUS_TITLE_REGEXES = [
    /focus/i,
    /deep\s*work/i,
    /no\s*meetings/i,
    /\bDND\b/,
    /do\s*not\s*disturb/i,
    /\bprotected\b/i,
  ];
  // Title patterns alone are NOT the rule. The backend settled this in
  // FocusTimeAllocationService.IsDetectedFocusEvent on 2026-07-05 after an
  // 8-person "hold" showed up as focus: an event only counts as auto-detected
  // focus if it looks like focus AND has at most 2 invitees. A solo hold, or
  // one with a room resource / one other person, is someone protecting time;
  // anything larger is a real meeting that happens to contain the word.
  //
  // This file mirrored the regex list but not the guard, so titles like
  // "Attendance Mandatory - On Hold Projects" were still being treated as
  // focus here (reported 2026-08-29). Word boundaries don't help — "Hold" is
  // a genuine word in that title; the attendee count is what separates them.
  const MAX_DETECTED_FOCUS_ATTENDEES = 2;

  // Attendees is a "; "-joined string from the API; empty/null = solo hold.
  // Rows synced before 2026-09-18 by the Microsoft/Google paths are
  // ", "-joined — count those on commas (mirrors CalendarFocusDetection).
  const attendeeCount = (attendees?: string) => {
    if (!attendees || !attendees.trim()) return 0;
    const sep = attendees.includes(';') ? ';' : ',';
    return attendees.split(sep).map(a => a.trim()).filter(Boolean).length;
  };

  const isFocusShaped = (title: string, attendees?: string) =>
    FOCUS_TITLE_REGEXES.some(rx => rx.test(title))
    && attendeeCount(attendees) <= MAX_DETECTED_FOCUS_ATTENDEES;

  const seenSignatures = new Set<string>();

  gridEvents.forEach(evt => {
    const start = new Date(evt.startTime);
    const end = new Date(evt.endTime);
    const yyyy = start.getFullYear();
    const mm = String(start.getMonth() + 1).padStart(2, '0');
    const dd = String(start.getDate()).padStart(2, '0');
    const ds = `${yyyy}-${mm}-${dd}`;
    if (!meetingsByDateStr[ds]) return;
    const sig = `${ds}|${start.getHours()}:${start.getMinutes()}|${end.getHours()}:${end.getMinutes()}|${evt.title.trim().toLowerCase()}`;
    if (seenSignatures.has(sig)) return;
    seenSignatures.add(sig);
    const evStart = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
    const evEnd = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
    if (isFocusShaped(evt.title, evt.attendees)) {
      const blockExistsAtSlot = focusBlocks.some(b =>
        b.date === ds && b.startTime.startsWith(evStart) && b.endTime.startsWith(evEnd),
      );
      if (blockExistsAtSlot) return;
    }
    const startMinute = (start.getHours() - DAY_START_HOUR) * 60 + start.getMinutes();
    const endMinute = (end.getHours() - DAY_START_HOUR) * 60 + end.getMinutes();
    if (endMinute <= 0 || startMinute >= TOTAL_MINUTES) return;
    meetingsByDateStr[ds].push({
      id: evt.id,
      startMinute: Math.max(0, startMinute),
      endMinute: Math.min(TOTAL_MINUTES, endMinute),
      title: evt.title,
      isMarkedAsFocus: linkedEventIds.has(evt.id),
    });
  });

  const minutesFromDayStart = (t: string): number => {
    const [h, m] = t.split(':').map(Number);
    return (h - DAY_START_HOUR) * 60 + (m || 0);
  };
  const findEventById = (id: string): CalendarEvent | undefined =>
    gridEvents.find(e => e.id === id);

  // Summary stats
  const totalFocusMinutes = focusBlocks.reduce((sum, b) => {
    const s = timeStrToHour(b.startTime);
    const e = timeStrToHour(b.endTime);
    return sum + (e - s) * 60;
  }, 0);
  const totalFocusHours = Math.round(totalFocusMinutes / 60 * 10) / 10;
  const daysWithFocus = new Set(focusBlocks.map(b => b.date)).size;
  const autoBlocks = focusBlocks.filter(b => b.source === 'AutoAllocated').length;
  const manualBlocks = focusBlocks.filter(b => b.source === 'Manual').length;

  const isCurrentWeek = formatDateOnly(getMonday(new Date())) === formatDateOnly(weekStart);

  return (
    <div>
      <PageHeader
        title="Focus Time"
        subtitle={`Week of ${formatShortDate(weekStart)} to ${formatShortDate(weekEnd)}`}
        action={
          <>
            <Button variant="ghost" size="icon" onClick={() => setWeekStart(d => addDays(d, -7))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={isCurrentWeek}
              onClick={() => setWeekStart(getMonday(new Date()))}
            >
              This week
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setWeekStart(d => addDays(d, 7))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button onClick={() => openAddDialog()} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add Block
            </Button>
          </>
        }
      />

      {/* Summary */}
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-4 pt-6">
          <div>
            <p className="text-xs text-muted-foreground">Focus This Week</p>
            <p className="text-2xl font-semibold">{totalFocusHours}h</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Blocks</p>
            <p className="text-2xl font-semibold">{focusBlocks.length}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Days Covered</p>
            <p className="text-2xl font-semibold">{daysWithFocus} / 5</p>
          </div>
          <div className="border-l border-border pl-4">
            <p className="text-xs text-muted-foreground">Auto-allocated</p>
            <p className="text-2xl font-semibold">{autoBlocks}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Manual</p>
            <p className="text-2xl font-semibold">{manualBlocks}</p>
          </div>
          {totalFocusHours > 0 && (
            <div className="border-l border-border pl-4">
              <p className="text-xs text-muted-foreground">% of 40h week</p>
              <p className="text-2xl font-semibold">{Math.round((totalFocusHours / 40) * 100)}%</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* First-run explainer — only when this week has no blocks. */}
      {!loading && focusBlocks.length === 0 && (
        <EmptyState
          variant="banner"
          icon={Shield}
          title={focusTimeCopy.firstRunHeading}
          body={focusTimeCopy.firstRunBody}
          action={{
            label: focusTimeCopy.firstRunAction,
            onClick: () => openAddDialog(),
            icon: Plus,
          }}
          className="mb-4"
        />
      )}

      <Alert className="mb-4 border-primary/15 bg-primary/[0.06]">
        <div className="flex items-start gap-2">
          <Shield className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <AlertDescription>
            <strong>Focus blocks protect your thinking time.</strong> The scheduling engine places work items
            in non-focus slots first. Focus blocks are only used as overflow when all other time is full.
            Run <strong>Start My Day</strong> to auto-allocate fresh blocks each morning.
          </AlertDescription>
        </div>
      </Alert>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {/* Weekly Grid */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="pt-6">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold text-muted-foreground">Weekly Focus Grid</p>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>

              {/* Legend */}
              <div className="mb-2 flex flex-wrap gap-3">
                {([
                  ['Auto-allocated', SOURCE_COLOURS.AutoAllocated, null],
                  ['Manual', SOURCE_COLOURS.Manual, null],
                  ['Detected', SOURCE_COLOURS.DetectedFromCalendar, null],
                  ['Meeting', MEETING_COLOUR, null],
                  ['Conflict', CONFLICT_COLOUR, '#FF5252'],
                ] as Array<[string, string, string | null]>).map(([label, bg, border]) => (
                  <div key={label} className="flex items-center gap-1">
                    <span
                      className="h-3 w-3 rounded-sm"
                      style={{ background: bg, ...(border ? { border: `1px solid ${border}` } : {}) }}
                    />
                    <span className="text-xs text-muted-foreground">{label}</span>
                  </div>
                ))}
              </div>

              <div className="overflow-x-auto">
                <div
                  className="grid"
                  style={{ gridTemplateColumns: '52px repeat(5, 1fr)', minWidth: 540 }}
                >
                  <div />
                  {DAYS.map((day, i) => (
                    <div key={day} className="border-b border-border py-2 text-center">
                      <p className="text-xs font-semibold text-muted-foreground">
                        {day.substring(0, 3)}
                      </p>
                      <p className="block text-[0.65rem] text-muted-foreground/70">
                        {formatShortDate(weekDates[i])}
                      </p>
                    </div>
                  ))}

                  {/* Hour gutter */}
                  <div className="relative" style={{ height: GRID_HEIGHT }}>
                    {HOURS.map((hour, idx) => (
                      <div
                        key={hour}
                        className="absolute right-1.5 w-full text-right"
                        style={{ top: idx * ROW_HEIGHT }}
                      >
                        <span className="text-[0.65rem] text-muted-foreground">{formatHour(hour)}</span>
                      </div>
                    ))}
                  </div>

                  {/* One column per weekday */}
                  {weekDates.map((date) => {
                    const ds = formatDateOnly(date);
                    const dayBlocks = blocksByDateStr[ds] || [];
                    const dayMeetings = meetingsByDateStr[ds] || [];
                    return (
                      <div
                        key={ds}
                        className="relative border-l border-border"
                        style={{
                          height: GRID_HEIGHT,
                          background: `repeating-linear-gradient(to bottom, transparent 0, transparent ${ROW_HEIGHT - 1}px, rgba(127,127,127,0.12) ${ROW_HEIGHT - 1}px, rgba(127,127,127,0.12) ${ROW_HEIGHT}px)`,
                        }}
                      >
                        {/* Focus blocks */}
                        {dayBlocks.map(block => {
                          const startMin = minutesFromDayStart(block.startTime);
                          const endMin = minutesFromDayStart(block.endTime);
                          if (endMin <= 0 || startMin >= TOTAL_MINUTES) return null;
                          const top = (Math.max(0, startMin) / 60) * ROW_HEIGHT;
                          const height = ((Math.min(TOTAL_MINUTES, endMin) - Math.max(0, startMin)) / 60) * ROW_HEIGHT;
                          const overlappingMeeting = dayMeetings.find(
                            m => !m.isMarkedAsFocus && m.startMinute < endMin && m.endMinute > startMin
                          );
                          const isConflict =
                            !!overlappingMeeting && !acknowledgedConflicts.has(block.id);
                          const bg = isConflict
                            ? CONFLICT_COLOUR
                            : SOURCE_COLOURS[block.source] ?? SOURCE_COLOURS.AutoAllocated;

                          const cell = (
                            <div
                              role={isConflict ? 'button' : undefined}
                              tabIndex={isConflict ? 0 : undefined}
                              onKeyDown={
                                isConflict
                                  ? (e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setConflictOpenId(block.id);
                                      }
                                    }
                                  : undefined
                              }
                              className={cn(
                                'absolute flex items-start gap-0.5 overflow-hidden rounded-sm border px-1 py-0.5',
                                isConflict ? 'cursor-pointer border-rose-500' : 'border-white/10',
                              )}
                              style={{
                                top,
                                left: 2,
                                right: 2,
                                height,
                                background: bg,
                                zIndex: 1,
                              }}
                            >
                              {!isConflict && (
                                <Lock className="mt-0.5 h-3 w-3 shrink-0 opacity-70" />
                              )}
                              <span
                                className={cn(
                                  'flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[0.6rem]',
                                  isConflict ? 'font-bold text-rose-500' : 'font-medium',
                                )}
                              >
                                {isConflict ? `⚠ ${overlappingMeeting!.title}` : block.label}
                              </span>
                            </div>
                          );

                          if (!isConflict) {
                            const linked = block.linkedWorkItems ?? [];
                            const hasNotes = !!(block.notes && block.notes.trim().length > 0);
                            const indicatorCount = linked.length + (hasNotes ? 1 : 0);
                            return (
                              <Popover
                                key={block.id}
                                open={noteOpenId === block.id}
                                onOpenChange={(o) => {
                                  if (o) openBlockDetails(block);
                                  else setNoteOpenId(null);
                                }}
                              >
                                <PopoverTrigger asChild>
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openBlockDetails(block);
                                      }
                                    }}
                                    className="cursor-pointer"
                                    style={{ position: 'absolute', inset: 0 }}
                                  >
                                    {cell}
                                    {indicatorCount > 0 && (
                                      <span
                                        className="absolute right-1 top-1 inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-foreground/80 px-1 text-[0.55rem] font-bold leading-none text-background"
                                        title={`${linked.length} task${linked.length === 1 ? '' : 's'}${hasNotes ? ' + notes' : ''}`}
                                      >
                                        {indicatorCount}
                                      </span>
                                    )}
                                  </div>
                                </PopoverTrigger>
                                <PopoverContent className="w-80" align="start">
                                  <div className="space-y-3">
                                    <div>
                                      <p className="text-sm font-semibold">{block.label}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {block.startTime.slice(0, 5)} — {block.endTime.slice(0, 5)} ·{' '}
                                        {Math.round((endMin - startMin) / 60 * 10) / 10}h
                                      </p>
                                    </div>
                                    {linked.length > 0 && (
                                      <div>
                                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                          Scheduled inside ({linked.length})
                                        </p>
                                        <ul className="mt-1 space-y-1">
                                          {linked.map(item => (
                                            <li key={item.id} className="flex items-baseline justify-between gap-2 text-sm">
                                              <span className="truncate">{item.title}</span>
                                              {item.estimatedMinutes != null && (
                                                <span className="shrink-0 text-xs text-muted-foreground">{item.estimatedMinutes}m</span>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    <div>
                                      <Label htmlFor={`notes-${block.id}`} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        <NotebookPen className="h-3 w-3" /> Notes
                                      </Label>
                                      <textarea
                                        id={`notes-${block.id}`}
                                        value={noteDraft}
                                        onChange={(e) => setNoteDraft(e.target.value)}
                                        rows={3}
                                        maxLength={2000}
                                        placeholder="What are you actually doing in this block?"
                                        className="mt-1 w-full resize-none rounded-md border border-input bg-transparent px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                      />
                                      <div className="mt-1 flex items-center justify-end gap-2">
                                        <Button size="sm" variant="ghost" onClick={() => setNoteOpenId(null)}>Cancel</Button>
                                        <Button size="sm" onClick={() => saveBlockNotes(block)} disabled={noteSaving} className="gap-1.5">
                                          {noteSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                          Save
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            );
                          }

                          return (
                            <Popover
                              key={block.id}
                              open={conflictOpenId === block.id}
                              onOpenChange={(o) => setConflictOpenId(o ? block.id : null)}
                            >
                              <PopoverTrigger asChild>{cell}</PopoverTrigger>
                              <PopoverContent className="max-w-xs">
                                <p className="mb-1 text-sm font-bold">{focusTimeCopy.conflictTitle}</p>
                                <p className="mb-2 text-sm text-muted-foreground">
                                  "{overlappingMeeting!.title}" overlaps this focus block.
                                </p>
                                <div className="flex flex-col gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleKeepFocus(block.id)}
                                    className="gap-1.5"
                                  >
                                    <Shield className="h-4 w-4" />
                                    {focusTimeCopy.conflictKeep}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRemoveFocus(block.id)}
                                    className="gap-1.5 border-rose-500/40 text-rose-500 hover:bg-rose-500/10"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    {focusTimeCopy.conflictRemove}
                                  </Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          );
                        })}

                        {/* Meetings — drawn unless the meeting IS the source of a focus block */}
                        {dayMeetings.filter(m => !m.isMarkedAsFocus).map(m => {
                          const top = (m.startMinute / 60) * ROW_HEIGHT;
                          const height = Math.max(14, ((m.endMinute - m.startMinute) / 60) * ROW_HEIGHT);
                          const evt = findEventById(m.id);
                          const tooltip = `${m.title}. Click to mark as focus time.`;

                          const cell = (
                            <div
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setMeetingOpenId(m.id);
                                }
                              }}
                              className="absolute flex cursor-pointer items-center gap-0.5 overflow-hidden rounded-sm border border-l-[3px] border-slate-400/40 px-1 py-0.5"
                              style={{
                                top,
                                left: 6,
                                right: 6,
                                height,
                                background: MEETING_COLOUR,
                                zIndex: 2,
                              }}
                            >
                              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[0.6rem] font-medium">
                                {m.title}
                              </span>
                            </div>
                          );

                          if (!evt) {
                            return (
                              <TooltipProvider key={m.id}>
                                <Tooltip>
                                  <TooltipTrigger asChild>{cell}</TooltipTrigger>
                                  <TooltipContent>{tooltip}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          }

                          return (
                            <Popover
                              key={m.id}
                              open={meetingOpenId === m.id}
                              onOpenChange={(o) => setMeetingOpenId(o ? m.id : null)}
                            >
                              <PopoverTrigger asChild>{cell}</PopoverTrigger>
                              <PopoverContent className="max-w-xs">
                                <p className="mb-1 text-sm font-bold">{evt.title}</p>
                                <p className="mb-2 text-sm text-muted-foreground">
                                  {linkedEventIds.has(evt.id)
                                    ? 'This meeting is currently marked as focus time. Unmark to treat it as a normal meeting again.'
                                    : 'Mark this meeting as focus time so the scheduler treats it as protected and the planner counts it toward your focus hours.'}
                                </p>
                                {linkedEventIds.has(evt.id) ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUnmarkMeetingAsFocus(evt)}
                                    className="w-full gap-1.5"
                                  >
                                    <BookmarkX className="h-4 w-4" />
                                    Unmark as focus
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => handleMarkMeetingAsFocus(evt)}
                                    className="w-full gap-1.5"
                                  >
                                    <Bookmark className="h-4 w-4" />
                                    Mark as focus
                                  </Button>
                                )}
                              </PopoverContent>
                            </Popover>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Blocks list */}
        <div>
          <Card className="h-full">
            <CardContent className="pt-6">
              <p className="mb-2 text-sm font-semibold text-muted-foreground">This Week's Blocks</p>

              {loading && (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {!loading && focusBlocks.length === 0 && (
                <div className="py-2 text-center">
                  <Sparkles className="mx-auto mb-1 h-8 w-8 text-muted-foreground/70" />
                  <p className="text-sm text-muted-foreground">No focus blocks this week</p>
                  <p className="text-xs text-muted-foreground/70">
                    Run Start My Day to auto-allocate, or add one manually
                  </p>
                </div>
              )}

              <div className="mt-2 flex flex-col gap-1">
                {focusBlocks
                  .slice()
                  .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
                  .map(block => {
                    const blockDate = new Date(block.date + 'T00:00:00');
                    const dayName = blockDate.toLocaleDateString('en-GB', { weekday: 'short' });
                    const dateStr = formatShortDate(blockDate);
                    const startH = block.startTime.substring(0, 5);
                    const endH = block.endTime.substring(0, 5);
                    const durationMins = Math.round(
                      (timeStrToHour(block.endTime) - timeStrToHour(block.startTime)) * 60
                    );

                    return (
                      <div
                        key={block.id}
                        className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 p-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{block.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {dayName} {dateStr} · {startH} to {endH} ({durationMins}m)
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge
                            variant="secondary"
                            className="h-[18px] text-[0.65rem]"
                            style={{ background: SOURCE_COLOURS[block.source] ?? undefined }}
                          >
                            {sourceLabel(block.source)}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleDelete(block.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {focusBlocks.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAddDialog()}
                  className="mt-3 w-full gap-1.5"
                >
                  <Plus className="h-4 w-4" />
                  Add manual block
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add manual block dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Focus Block</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fb-date">Date</Label>
              <Input
                id="fb-date"
                type="date"
                value={addDate}
                onChange={e => setAddDate(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="fb-start">Start</Label>
                <Input
                  id="fb-start"
                  type="time"
                  value={addStart}
                  onChange={e => setAddStart(e.target.value)}
                />
              </div>
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="fb-end">End</Label>
                <Input
                  id="fb-end"
                  type="time"
                  value={addEnd}
                  onChange={e => setAddEnd(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fb-label">Label (optional)</Label>
              <Input
                id="fb-label"
                value={addLabel}
                onChange={e => setAddLabel(e.target.value)}
                placeholder="Focus Time"
              />
            </div>
            {addError && (
              <Alert variant="error">
                <AlertDescription>{addError}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveBlock} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Snackbar — fixed-position banner that auto-dismisses */}
      {snackbar && (
        <div
          role="status"
          className="fixed inset-x-0 bottom-4 z-50 mx-auto w-fit max-w-md rounded-lg border border-border bg-card px-4 py-2 text-sm shadow-lg"
        >
          {snackbar}
        </div>
      )}
    </div>
  );
}
